import React from "react";
import { Tooltip } from "antd";

// H2 — Song History summary badges and truncate helpers.
// Labels match admin-api song-history-summary ACTIVITY_LABELS / OUTCOME_LABELS.

type BadgeTone = "green" | "amber" | "blue" | "grey" | "orange";

const TONE_STYLE: Record<BadgeTone, React.CSSProperties> = {
  green: { background: "#d1e7dd", color: "#0f5132" },
  amber: { background: "#fff3cd", color: "#664d03" },
  blue: { background: "#cfe2ff", color: "#084298" },
  grey: { background: "#e2e3e5", color: "#41464b" },
  orange: { background: "#ffe5d0", color: "#8a3c00" },
};

function badge(label: string, tone: BadgeTone): React.ReactElement {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.25,
        whiteSpace: "nowrap",
        ...TONE_STYLE[tone],
      }}
    >
      {label}
    </span>
  );
}

const ACTIVITY_TONE: Record<string, BadgeTone> = {
  original_upload: "blue",
  audio_reprocessed: "orange",
  song_audio_replaced: "orange",
  archived: "grey",
  restored: "green",
  metadata_corrected: "amber",
  permanently_deleted: "grey",
};

const OUTCOME_TONE: Record<string, BadgeTone> = {
  active_playable: "green",
  archived: "grey",
  active_not_playable: "amber",
  deleted_tombstone: "grey",
};

export const ACTIVITY_FILTER_OPTIONS = [
  { value: "original_upload", label: "Original Upload" },
  { value: "audio_reprocessed", label: "Audio Reprocessed" },
  { value: "song_audio_replaced", label: "Song Audio Replaced" },
  { value: "archived", label: "Archived" },
  { value: "restored", label: "Restored" },
  { value: "metadata_corrected", label: "Metadata Corrected" },
  { value: "permanently_deleted", label: "Permanently Deleted" },
] as const;

export const OUTCOME_FILTER_OPTIONS = [
  { value: "active_playable", label: "Active / Playable" },
  { value: "active_not_playable", label: "Active / Not Playable" },
  { value: "archived", label: "Archived" },
  { value: "deleted_tombstone", label: "Deleted / Tombstone" },
] as const;

export function activityFilterOptionsForTab(
  status: "active" | "archived" | "deleted",
): ReadonlyArray<{ value: string; label: string }> {
  if (status === "deleted") {
    return ACTIVITY_FILTER_OPTIONS.filter(
      (o) =>
        o.value === "permanently_deleted" ||
        o.value === "archived" ||
        o.value === "original_upload" ||
        o.value === "audio_reprocessed" ||
        o.value === "song_audio_replaced" ||
        o.value === "metadata_corrected" ||
        o.value === "restored",
    );
  }
  if (status === "archived") {
    return ACTIVITY_FILTER_OPTIONS.filter((o) => o.value !== "permanently_deleted");
  }
  return ACTIVITY_FILTER_OPTIONS.filter(
    (o) => o.value !== "permanently_deleted" && o.value !== "archived",
  );
}

export function outcomeFilterOptionsForTab(
  status: "active" | "archived" | "deleted",
): ReadonlyArray<{ value: string; label: string }> {
  // Outcome is only meaningful on Active — Archived/Deleted tabs already
  // define lifecycle state, so a single-value outcome filter is redundant.
  if (status !== "active") return [];
  return OUTCOME_FILTER_OPTIONS.filter(
    (o) => o.value === "active_playable" || o.value === "active_not_playable",
  );
}

export function renderActivityBadge(
  type: string | null | undefined,
  label?: string | null,
): React.ReactElement {
  if (!type) return <span>—</span>;
  const text = label || type;
  return badge(text, ACTIVITY_TONE[type] ?? "grey");
}

export function renderOutcomeBadge(
  outcome: string | null | undefined,
  label?: string | null,
): React.ReactElement {
  if (!outcome) return <span>—</span>;
  const text = label || outcome;
  return badge(text, OUTCOME_TONE[outcome] ?? "grey");
}

/** Song History Detail — lifecycle partition badge (Active / Archived / Deleted). */
export function renderLifecycleStatusBadge(
  status: string | null | undefined,
): React.ReactElement {
  const s = (status || "").trim().toLowerCase();
  if (s === "active") return badge("Active", "green");
  if (s === "archived") return badge("Archived", "grey");
  if (s === "deleted") return badge("Deleted", "grey");
  return <span>—</span>;
}

/** Truncate long cell text; full value on hover tooltip. */
export function TruncateCell({
  text,
  className,
}: {
  text: string | null | undefined;
  className?: string;
}): React.ReactElement {
  const value = (text || "").trim() || "—";
  if (value === "—") return <span className={className}>—</span>;
  return (
    <Tooltip title={value} placement="topLeft">
      <span className={className}>{value}</span>
    </Tooltip>
  );
}

const TRACK_ID_RE =
  /^track_[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isFullTrackId(value: string): boolean {
  return TRACK_ID_RE.test(value.trim());
}
